import { extOf } from "./format";

import type { KbNode } from "@/shared/types";

/* The three ways a file can be read: as uploaded (Original), as extracted (Raw), and as
   the agent gets it (Indexed). Which apply depends on what the browser can draw and on
   whether any text was extracted. */
export type PreviewTab = "original" | "raw" | "indexed";
export type PreviewKind = "pdf" | "image" | "markdown" | "sheet" | "text" | "office";

export const TAB_LABEL: Record<PreviewTab, string> = { original: "Original", raw: "Raw", indexed: "Indexed" };
export const TAB_HINT: Record<PreviewTab, string> = {
  original: "The file as uploaded",
  raw: "The text extracted from it",
  indexed: "What the agent reads",
};

const MARKDOWN = ["md", "markdown"];
const SHEET = ["csv", "tsv"];
const TEXT = ["txt", "json", "log", "yaml", "yml", "xml", "html", "css", "js", "ts", "tsx", "jsx", "py", "sql", "sh"];

export function kindOf(node: KbNode): PreviewKind {
  const ext = extOf(node.name);
  const mime = node.file?.mime ?? "";
  // pdf and image come from the stored mime only: a name can claim anything, and these two
  // kinds are rendered by the browser itself
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("image/")) return "image";
  if (MARKDOWN.includes(ext) || mime === "text/markdown") return "markdown";
  if (SHEET.includes(ext)) return "sheet";
  if (TEXT.includes(ext) || mime.startsWith("text/") || mime === "application/json") return "text";
  return "office";
}

/* Original for markdown is the text rendered; for a sheet or an Office file it is drawn
   from the bytes by the shared viewer. Plain text has no Original apart from its Raw. */
const TABS: Record<PreviewKind, PreviewTab[]> = {
  pdf: ["original", "raw", "indexed"],
  image: ["original"],
  markdown: ["original", "raw", "indexed"],
  sheet: ["original", "raw", "indexed"],
  text: ["raw", "indexed"],
  office: ["original", "raw", "indexed"],
};

/** Tabs for a file, first = default. `hasText` is false once `content` answered 404:
    only what is drawn from the bytes is left. */
export function tabsFor(node: KbNode, hasText = true): PreviewTab[] {
  const kind = kindOf(node);
  if (hasText) return TABS[kind];
  return kind === "markdown" || kind === "text" ? [] : ["original"];
}
